import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Copy, Download, LayoutGrid, List, Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@tes/ui';
import { api } from '../../lib/api';
import { runSequential, type BulkOutcome } from '../../lib/bulk';
import { downloadJson, slugify } from '../../lib/download';
import { messageOf } from '../../lib/errors';
import { useBulkSelection } from '../../lib/useBulkSelection';
import { useLocalStorage } from '../../lib/useLocalStorage';
import type { TemplateSummary } from '../../lib/types';
import { BatchNotice, BulkBar } from '../../components/BulkActions';
import { ImportTemplateDialog } from '../../components/ImportTemplateDialog';
import { BulkDeleteTemplatesDialog, DeleteTemplateDialog, NewTemplateDialog } from './dialogs';
import { TemplateCard, type RowActions } from './TemplateCard';
import { TemplateTable } from './TemplateTable';

/** Stable identity so selection memos don't rebuild on every loading render. */
const NO_TEMPLATES: TemplateSummary[] = [];
const templateId = (t: TemplateSummary) => t.id;

type ViewMode = 'grid' | 'list';

const stamp = () => new Date().toISOString().slice(0, 10);

export default function TemplatesListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['templates'], queryFn: api.listTemplates });
  const [view, setView] = useLocalStorage<ViewMode>('tes.templates.view', 'grid');
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [deleting, setDeleting] = useState<TemplateSummary | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState<TemplateSummary[] | null>(null);

  const templates = data ?? NO_TEMPLATES;
  const { sel, picked, batch, dismissBatch, finishBatch } = useBulkSelection(templates, templateId);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['templates'] });

  // The copy is always a fresh draft, so open it straight away — you duplicate
  // a template in order to change something in it.
  const duplicate = useMutation({
    mutationFn: (id: string) => api.duplicateTemplate(id),
    onSuccess: (copy) => {
      refresh();
      navigate(`/templates/${copy.id}`);
    },
  });

  const exportOne = useMutation({
    mutationFn: async (t: TemplateSummary) => ({ t, doc: await api.exportTemplate(t.id) }),
    onSuccess: ({ t, doc }) => downloadJson(`${slugify(t.name)}.template.json`, doc),
  });

  const exportAll = useMutation({
    mutationFn: () => api.exportAllTemplates(),
    onSuccess: (doc) => downloadJson(`templates-${stamp()}.json`, doc),
  });

  // Every bulk action takes its items as a mutation VARIABLE rather than reading
  // the selection from the closure, so a refetch landing mid-run can't change
  // what the batch is working on halfway through.
  const bulkExport = useMutation({
    mutationFn: (items: TemplateSummary[]) => api.exportTemplates(items.map((t) => t.id)),
    onSuccess: (doc) => downloadJson(`templates-selection-${stamp()}.json`, doc),
  });

  const finish = (verb: string) => (results: BulkOutcome[]) => {
    finishBatch(verb)(results);
    refresh();
  };

  const bulkDuplicate = useMutation({
    mutationFn: (items: TemplateSummary[]) =>
      runSequential(items, (t) => ({ id: t.id, name: t.name }), (t) => api.duplicateTemplate(t.id)),
    onSuccess: finish('duplicated'),
  });

  const bulkArchive = useMutation({
    mutationFn: (items: TemplateSummary[]) =>
      runSequential(items, (t) => ({ id: t.id, name: t.name }), (t) => api.archive(t.id)),
    onSuccess: finish('archived'),
  });

  const busy = bulkExport.isPending || bulkDuplicate.isPending || bulkArchive.isPending;
  // runSequential collects per-item failures rather than throwing, so the batch
  // mutations' own `error` only fires on something unexpected — surfaced here so
  // it can't fail silently.
  const actionError =
    duplicate.error ??
    exportOne.error ??
    exportAll.error ??
    bulkExport.error ??
    bulkDuplicate.error ??
    bulkArchive.error ??
    null;

  const actionsFor = (t: TemplateSummary): RowActions => ({
    onOpen: () => navigate(`/templates/${t.id}`),
    onDuplicate: () => duplicate.mutate(t.id),
    onExport: () => exportOne.mutate(t),
    onDelete: () => setDeleting(t),
  });

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Templates</h1>
        <div className="flex items-center gap-2">
          <ViewToggle value={view} onChange={setView} />
          <Button
            variant="outline"
            onClick={() => exportAll.mutate()}
            disabled={exportAll.isPending || templates.length === 0}
            title="Download every template's design as one JSON backup"
          >
            <Download size={16} className="mr-1" />
            {exportAll.isPending ? 'Exporting…' : 'Export all'}
          </Button>
          <Button variant="outline" onClick={() => setShowImport(true)}>
            <Upload size={16} className="mr-1" /> Import
          </Button>
          <Button onClick={() => setShowNew(true)}>
            <Plus size={16} className="mr-1" /> New template
          </Button>
        </div>
      </div>

      {actionError && (
        <p className="mb-3 text-sm text-red-600">{messageOf(actionError, 'Action failed')}</p>
      )}

      <BatchNotice batch={batch} onDismiss={dismissBatch} />

      <BulkBar
        count={sel.count}
        noun="template"
        total={templates.length}
        onSelectAll={() => sel.setAll(true)}
        onClear={sel.clear}
      >
        <Button size="sm" variant="outline" disabled={busy} onClick={() => bulkExport.mutate(picked)}>
          <Download size={14} className="mr-1" /> Export
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => bulkDuplicate.mutate(picked)}
        >
          <Copy size={14} className="mr-1" /> Duplicate
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => bulkArchive.mutate(picked)}
          title="Stops new generation; existing documents are unaffected"
        >
          <Archive size={14} className="mr-1" /> Archive
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => setBulkDeleting(picked)}>
          <Trash2 size={14} className="mr-1" /> Delete
        </Button>
      </BulkBar>

      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
          No templates yet. Create one to start placing fields.
        </div>
      ) : view === 'grid' ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              selected={sel.has(t.id)}
              onToggle={(o) => sel.toggle(t.id, o)}
              actions={actionsFor(t)}
            />
          ))}
        </div>
      ) : (
        <TemplateTable templates={templates} sel={sel} actionsFor={actionsFor} />
      )}

      {showNew && (
        <NewTemplateDialog
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            refresh();
            navigate(`/templates/${id}`);
          }}
        />
      )}

      {showImport && (
        <ImportTemplateDialog
          // The dialog invalidates the list itself when an import lands, so
          // closing is just closing.
          onClose={() => setShowImport(false)}
          onImported={(result) => {
            setShowImport(false);
            // One template imported → go straight into it; a bulk restore stays
            // on the list so you can see everything that landed.
            if (result.imported === 1) navigate(`/templates/${result.templates[0].templateId}`);
          }}
        />
      )}

      {deleting && (
        <DeleteTemplateDialog
          template={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            refresh();
          }}
        />
      )}

      {bulkDeleting && (
        <BulkDeleteTemplatesDialog
          templates={bulkDeleting}
          onClose={() => setBulkDeleting(null)}
          onDeleted={(results) => {
            setBulkDeleting(null);
            finish('deleted')(results);
          }}
        />
      )}
    </div>
  );
}

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const opt = (mode: ViewMode, Icon: typeof LayoutGrid, label: string) => (
    <button
      type="button"
      key={mode}
      onClick={() => onChange(mode)}
      aria-label={label}
      aria-pressed={value === mode}
      title={label}
      className={`rounded-md px-2 py-1.5 ${
        value === mode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon size={16} />
    </button>
  );
  return (
    <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5">
      {opt('grid', LayoutGrid, 'Grid view')}
      {opt('list', List, 'List view')}
    </div>
  );
}
